import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Permissions, Roles } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api } from '../lib/api'
import OrgMultiSelect from '../components/OrgMultiSelect'
import { useToast } from '../ui/ToastProvider'

export default function NoticeNewPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { guard, allow } = useAccess()
    const { show } = useToast()
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [targets, setTargets] = useState([]) // 선택된 대상 조직 ID 배열 (number)
    const [mappedGaOptions, setMappedGaOptions] = useState([])
    const [mappedGaLoading, setMappedGaLoading] = useState(false)
    const [attachFiles, setAttachFiles] = useState([]) // 공지 작성 시 함께 첨부할 파일들
    const editorRef = useRef(null)

    const roles = Array.isArray(user?.roles) ? user.roles : []
    const isOperator = roles.includes(Roles.OPERATOR)
    const isInsurer =
        roles.includes(Roles.INSURER_LEADER) ||
        roles.includes(Roles.INSURER_BRANCH_MANAGER) ||
        roles.includes(Roles.INSURER_MANAGER) ||
        roles.includes(Roles.INSURER_PLAN_MANAGER)

    // 원수사 계정: 소속 조직에 매핑된 GA 목록 로딩
    useEffect(() => {
        let ignore = false
        async function loadMappedGAs() {
            if (!isInsurer || !user?.orgId) {
                if (!ignore) setMappedGaOptions([])
                return
            }
            setMappedGaLoading(true)
            try {
                // /orgs/{id}/mappings 는 "지점/팀"에서 조회해도 스코프(지점 루트/회사) 기준으로
                // 매핑된 GA 목록을 내려주므로 공지 대상 선택에 더 적합하다.
                const res = await api.get(`/orgs/${user.orgId}/mappings`)
                const data = res?.data?.data ?? res?.data ?? {}
                const mapped = Array.isArray(data.gas) ? data.gas : []
                if (!ignore) {
                    // { id, name } 형태로만 사용
                    setMappedGaOptions(mapped.map(g => ({ id: g.id, name: g.name })))
                }
            } catch {
                if (!ignore) setMappedGaOptions([])
            } finally {
                if (!ignore) setMappedGaLoading(false)
            }
        }
        loadMappedGAs()
        return () => { ignore = true }
    }, [isInsurer, user])

    const onSubmit = async (e) => {
        e.preventDefault()
        if (!allow.perm([Permissions.NOTICE_CREATE])) return
        try {
            const payload = { title, content, orgId: user?.orgId, creatorId: user?.id }
            if (targets.length > 0) {
                payload.targetOrgId = Number(targets[0])
                payload.targetOrgIds = targets.map(t => Number(t))
            }
            const res = await api.post('/notices', payload)
            const body = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const id = body?.id
            if (!id) {
                alert('작성은 완료되었으나 ID를 확인하지 못했습니다. 목록에서 확인해주세요.')
                return
            }

            // 공지 작성 시 함께 첨부한 파일들을 업로드 + 공지 첨부로 연결
            if (attachFiles.length > 0) {
                for (const file of attachFiles) {
                    try {
                        const form = new FormData()
                        form.append('file', file)
                        const up = await api.post('/files', form, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        const fileId = up?.data?.data?.fileId ?? up?.data?.fileId
                        if (!fileId) continue
                        await api.post(`/notices/${id}/attachments`, {
                            fileId,
                            filename: file.name,
                            contentType: file.type,
                            size: file.size,
                            uploadedByUserId: user?.id || null,
                        })
                    } catch {
                        // 개별 파일 실패는 전체 작성 실패로 보지 않고 넘어감
                    }
                }
            }

            show('공지 작성이 완료되었습니다.', 'success')
            navigate(`/notices/${id}`)
        } catch (err) {
            alert('작성에 실패했습니다')
        }
    }

    return (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-bold">새 공지</h1>
            <form onSubmit={onSubmit} className="mt-6 space-y-6 max-w-3xl">
                <div>
                    <label className="block text-sm">제목</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </div>
              
                <div>
                    <label className="block text-sm">대상 조직</label>
                    {/* 기본: 조직 트리 기반 멀티 선택 (운영자=전체, 그 외=내 조직+하위) */}
                    {isInsurer && !isOperator ? (
                        <div className="mt-1 space-y-4">
                            {/* 1) 내 조직(원수사) + 하위 조직 */}
                            <div>
                                <OrgMultiSelect
                                    value={targets}
                                    onChange={setTargets}
                                    placeholder="대상 조직 검색..."
                                    collapsible={true}
                                    defaultOpen={false}
                                    title="내 조직/하위 조직 선택"
                                    scope="mine"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    내 조직(원수사) 및 하위 조직 트리에서 대상 조직을 선택할 수 있습니다.
                                </p>
                            </div>

                            {/* 2) 매핑된 GA */}
                            <div>
                                <div className="text-sm font-medium mb-1">매핑된 GA 대상 선택</div>
                                {mappedGaLoading ? (
                                    <div className="text-xs text-slate-500">매핑된 GA 지점을 불러오는 중입니다...</div>
                                ) : mappedGaOptions.length === 0 ? (
                                    <div className="text-xs text-red-500">
                                        매핑된 GA 지점이 없습니다. 조직 관리에서 GA 매핑을 먼저 설정해 주세요.
                                    </div>
                                ) : (
                                    <div className="border rounded p-2 max-h-48 overflow-y-auto text-sm space-y-1">
                                        {mappedGaOptions.map(opt => (
                                            <label key={opt.id} className="flex items-center gap-2 text-xs">
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-slate-300"
                                                    checked={targets.includes(Number(opt.id))}
                                                    onChange={(e) => {
                                                        const idNum = Number(opt.id)
                                                        setTargets(prev => {
                                                            const set = new Set((prev || []).map(v => Number(v)))
                                                            if (e.target.checked) set.add(idNum)
                                                            else set.delete(idNum)
                                                            return Array.from(set)
                                                        })
                                                    }}
                                                />
                                                <span>{opt.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                    내 조직에 매핑된 GA 조직도 대상 조직으로 선택할 수 있습니다.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mt-1">
                                <OrgMultiSelect
                                    value={targets}
                                    onChange={setTargets}
                                    placeholder="대상 조직 검색..."
                                    collapsible={true}
                                    defaultOpen={false}
                                    title="대상 조직 선택"
                                    scope={Array.isArray(user?.roles) && user.roles.includes('OPERATOR') ? 'all' : 'mine'}
                                />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                운영자는 전체 조직, 그 외에는 내 조직과 하위 조직만 선택할 수 있습니다.
                            </p>
                        </>
                    )}
                </div>
                <div>
                    <label className="block text-sm">내용</label>
                    {/* 간단 툴바 + contentEditable 기반 스마트 에디터 */}
                    <div className="mt-1 border rounded">
                        <div className="border-b px-2 py-1 flex gap-2 text-xs">
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={() => {
                                    if (!editorRef.current) return
                                    document.execCommand('bold')
                                    editorRef.current.focus()
                                }}
                            >
                                굵게
                            </button>
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={() => {
                                    if (!editorRef.current) return
                                    document.execCommand('insertUnorderedList')
                                    editorRef.current.focus()
                                }}
                            >
                                • 목록
                            </button>
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={async () => {
                                    const input = document.createElement('input')
                                    input.type = 'file'
                                    input.accept = 'image/*'
                                    input.onchange = async () => {
                                        const file = input.files && input.files[0]
                                        if (!file || !editorRef.current) return
                                        try {
                                            const form = new FormData()
                                            form.append('file', file)
                                            const up = await api.post('/files', form, {
                                                headers: { 'Content-Type': 'multipart/form-data' },
                                            })
                                            const fileId = up?.data?.data?.fileId ?? up?.data?.fileId
                                            if (!fileId) return
                                            // 에디터를 포커스하고 커서를 항상 에디터 내부(끝)에 두어,
                                            // 대상 조직 영역 등 다른 DOM에 이미지가 삽입되지 않도록 방지
                                            editorRef.current.focus()
                                            const sel = window.getSelection()
                                            if (sel) {
                                                const range = document.createRange()
                                                range.selectNodeContents(editorRef.current)
                                                range.collapse(false) // 끝으로 이동
                                                sel.removeAllRanges()
                                                sel.addRange(range)
                                            }
                                            // API baseURL (예: http://localhost:8080/v1)를 포함한 절대 경로로 이미지 src 설정
                                            const base = (api.defaults && api.defaults.baseURL) ? api.defaults.baseURL.replace(/\/$/, '') : ''
                                            const url = `${base}/files/${fileId}/preview`
                                            const img = document.createElement('img')
                                            img.src = url
                                            img.alt = file.name
                                            img.style.maxWidth = '100%'
                                            const currentSel = window.getSelection()
                                            if (currentSel && currentSel.rangeCount > 0) {
                                                const range = currentSel.getRangeAt(0)
                                                range.insertNode(img)
                                                range.collapse(false)
                                            } else {
                                                editorRef.current.appendChild(img)
                                            }
                                            // 상태 동기화
                                            setContent(editorRef.current.innerHTML)
                                        } catch {
                                            // TODO: 필요 시 토스트로 알림
                                        }
                                    }
                                    input.click()
                                }}
                            >
                                이미지
                            </button>
                        </div>
                        <div
                            ref={editorRef}
                            className="min-h-[160px] px-3 py-2 text-sm prose max-w-none focus:outline-none"
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => setContent(e.currentTarget.innerHTML)}
                        />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                        툴바를 사용해 간단한 서식을 지정하고 이미지 파일을 업로드해 본문 내에 바로 삽입할 수 있습니다. 첨부 파일(문서/PDF 등)은 공지 상세 화면에서 별도로 추가할 수 있습니다.
                    </p>
                </div>
                  {/* 작성 시 함께 첨부할 파일 목록 */}
                  <div>
                    <label className="block text-sm">첨부 파일</label>
                    <div className="mt-1 flex flex-col gap-2">
                        <input
                            type="file"
                            multiple
                            className="text-sm"
                            onChange={(e) => {
                                const files = Array.from(e.target.files || [])
                                if (files.length === 0) return
                                setAttachFiles(prev => [...prev, ...files])
                                e.target.value = ''
                            }}
                        />
                        {attachFiles.length > 0 && (
                            <ul className="text-xs text-slate-700 border rounded p-2 max-h-40 overflow-y-auto">
                                {attachFiles.map((f, idx) => (
                                    <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 py-0.5">
                                        <span className="truncate">{f.name}</span>
                                        <button
                                            type="button"
                                            className="text-red-500"
                                            onClick={() => {
                                                setAttachFiles(prev => prev.filter((_, i) => i !== idx))
                                            }}
                                        >
                                            제거
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="text-xs text-slate-500">
                            공지와 함께 업로드할 파일(PDF/이미지/문서 등)을 선택하면, 저장 시 자동으로 공지에 첨부됩니다.
                        </p>
                    </div>
                </div>
                <div className="pt-2">
                    <button {...guard.disableIfNoPerm([Permissions.NOTICE_CREATE])} className="rounded bg-brand-navy text-white px-4 py-2" type="submit">작성</button>
                </div>
            </form>
        </section>
    )
}


